import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@material-ui/core"
import { useEffect, useState } from "react"
import { alertStart } from "../../../routes"
import { obterFormasPagamento } from "../../../services/configuracaoFormasPagamento"
import { buscarPedidoParaEdicao, editarPedido } from "../../../services/pedidoEdicao"

// Converte o texto do campo (aceita vírgula) em Double ou null quando vazio/ inválido.
const paraNumero = valor => {
    if (valor === "" || valor === null || valor === undefined) return null
    const n = Number(String(valor).replace(",", "."))
    return isNaN(n) ? null : n
}

const FORM_VAZIO = {
    nomePedido: "",
    telefoneCliente: "",
    idFormaPagamento: "",
    valorDesconto: "",
    taxaAdicional: "",
    observacao: "",
    cpfTitular: "",
    mensagem: "",
}

/**
 * Diálogo de edição de um pedido. Ao abrir, busca o PedidoPojo completo (GET /pedido/{id})
 * e a lista de formas de pagamento, pré-preenche o formulário e, ao salvar, envia o
 * PedidoPojo para POST /pedido/editar. O backend só altera os campos enviados e registra
 * cada mudança no histórico do pedido.
 *
 * Props:
 *  - idPedido: id do pedido a editar (falsy = fechado)
 *  - authorization: credencial (chaveSistema)
 *  - onFechar(): fecha o diálogo
 *  - onSalvo(pedidoAtualizado): chamado após salvar com sucesso
 */
export default function DialogEditarPedido({ idPedido, authorization, onFechar, onSalvo }) {
    const open = !!idPedido
    const [carregando, setCarregando] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [pedido, setPedido] = useState(null) // PedidoPojo completo, base do payload
    const [formas, setFormas] = useState([])
    const [form, setForm] = useState(FORM_VAZIO)

    useEffect(() => {
        if (!idPedido) return
        let ativo = true
        setCarregando(true)
        setPedido(null)
        setForm(FORM_VAZIO)

        Promise.all([
            buscarPedidoParaEdicao(idPedido, authorization),
            obterFormasPagamento(authorization),
        ])
            .then(([resp, respFormas]) => {
                if (!ativo) return
                if (resp.retornoErro) {
                    alertStart(resp.mensagem || "Erro ao carregar o pedido.", "error")
                    onFechar?.()
                    return
                }
                const p = resp.pedido
                setPedido(p)
                setFormas(
                    Array.isArray(respFormas?.formasPagamento)
                        ? respFormas.formasPagamento.filter(f => f.associado)
                        : []
                )
                setForm({
                    nomePedido: p.nomePedido ?? "",
                    telefoneCliente: p.telefoneCliente ?? "",
                    idFormaPagamento: p.formaPagamento?.id ?? "",
                    valorDesconto: p.valorDesconto != null ? String(p.valorDesconto) : "",
                    taxaAdicional: p.taxaAdicional != null ? String(p.taxaAdicional) : "",
                    observacao: p.observacao ?? "",
                    cpfTitular: p.cpfTitular ?? "",
                    mensagem: "",
                })
            })
            .finally(() => {
                if (ativo) setCarregando(false)
            })

        return () => {
            ativo = false
        }
        // onFechar é estável o suficiente (definido no pai); recarrega ao trocar de pedido.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPedido, authorization])

    // Lê o valor ANTES do setState: no React 16 o evento sintético é reciclado e
    // e.target vira null quando o updater roda (derrubava o diálogo ao digitar).
    const atualizarCampo = campo => e => {
        const { value } = e.target
        setForm(prev => ({ ...prev, [campo]: value }))
    }

    const salvar = async () => {
        if (!pedido) return
        if (!form.idFormaPagamento) {
            alertStart("Selecione a forma de pagamento.", "warning")
            return
        }

        setSalvando(true)
        // Base = PedidoPojo completo (preserva campos sem guarda de null no backend, p/ ex.
        // codigoPais / agendamento, que seriam apagados se enviados vazios). Removemos itens e
        // formasPagamentoPedido para NÃO reprocessá-los (poluiria o histórico) e data porque o
        // round-trip da serialização poderia gerar um "Data alterada" falso.
        const payload = {
            ...pedido,
            itens: undefined,
            formasPagamentoPedido: undefined,
            data: undefined,
            nomePedido: form.nomePedido,
            telefoneCliente: form.telefoneCliente,
            formaPagamento: { id: Number(form.idFormaPagamento) },
            valorDesconto: paraNumero(form.valorDesconto),
            taxaAdicional: paraNumero(form.taxaAdicional),
            observacao: form.observacao,
            cpfTitular: form.cpfTitular,
            mensagem: form.mensagem?.trim() ? form.mensagem.trim() : undefined,
        }

        const resp = await editarPedido(payload, authorization)
        setSalvando(false)

        if (resp.retornoErro) {
            alertStart(resp.mensagem || "Erro ao salvar as alterações.", "error")
            return
        }
        alertStart("Pedido atualizado com sucesso.", "success")
        onSalvo?.(resp.pedido)
        onFechar?.()
    }

    // Garante que a forma atual do pedido apareça no select mesmo se não estiver entre as
    // associadas (ex.: forma desativada depois que o pedido foi feito).
    const formaAtualId = pedido?.formaPagamento?.id
    const formaAtualNaLista = formas.some(f => f.idFormaPagamento === formaAtualId)

    return (
        <Dialog open={open} onClose={salvando ? undefined : onFechar} maxWidth="xs" fullWidth>
            <DialogTitle>Editar Pedido{pedido?.id ? ` #${pedido.id}` : ""}</DialogTitle>
            <DialogContent dividers>
                {carregando || !pedido ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <TextField
                            label="Nome do cliente"
                            variant="outlined"
                            fullWidth
                            value={form.nomePedido}
                            onChange={atualizarCampo("nomePedido")}
                        />
                        <TextField
                            label="Telefone"
                            variant="outlined"
                            fullWidth
                            value={form.telefoneCliente}
                            onChange={atualizarCampo("telefoneCliente")}
                        />
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel id="editar-forma-pgto-label">Forma de pagamento</InputLabel>
                            <Select
                                labelId="editar-forma-pgto-label"
                                label="Forma de pagamento"
                                value={form.idFormaPagamento}
                                onChange={atualizarCampo("idFormaPagamento")}
                            >
                                {!formaAtualNaLista && formaAtualId ? (
                                    <MenuItem value={formaAtualId}>
                                        {pedido.formaPagamento?.descricao ||
                                            pedido.formaPagamento?.nome ||
                                            "Forma atual"}
                                    </MenuItem>
                                ) : null}
                                {formas.map(f => (
                                    <MenuItem key={f.idFormaPagamento} value={f.idFormaPagamento}>
                                        {f.descricaoFormaPagamento}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Desconto (R$)"
                            variant="outlined"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: "0.01" }}
                            value={form.valorDesconto}
                            onChange={atualizarCampo("valorDesconto")}
                        />
                        <TextField
                            label="Acréscimo / Taxa de serviço (R$)"
                            variant="outlined"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: "0.01" }}
                            value={form.taxaAdicional}
                            onChange={atualizarCampo("taxaAdicional")}
                        />
                        <TextField
                            label="Observação"
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.observacao}
                            onChange={atualizarCampo("observacao")}
                        />
                        <TextField
                            label="CPF do titular"
                            variant="outlined"
                            fullWidth
                            value={form.cpfTitular}
                            onChange={atualizarCampo("cpfTitular")}
                        />
                        <TextField
                            label="Motivo da alteração (opcional)"
                            variant="outlined"
                            fullWidth
                            value={form.mensagem}
                            onChange={atualizarCampo("mensagem")}
                            helperText="Fica registrado no histórico do pedido."
                        />
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onFechar} disabled={salvando}>
                    Cancelar
                </Button>
                <Button
                    onClick={salvar}
                    variant="contained"
                    color="primary"
                    disabled={salvando || carregando || !pedido}
                    startIcon={
                        salvando ? <CircularProgress size={18} color="inherit" /> : null
                    }
                >
                    {salvando ? "Salvando..." : "Salvar"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
